/* eslint-disable no-unused-vars, no-undef */
import { useEffect, useMemo, useState, FormEvent, ChangeEvent } from 'react';
import {
	Form,
	Container,
	Row,
	Col,
	FormControl,
	Button,
	CloseButton,
} from 'react-bootstrap';
import { BsQuestionCircle as QuestionCircle } from 'react-icons/bs';

import BayFilterDrawer from '../../BayFilterDrawer';
import BaySelect from '../../BaySelect';
import BayTooltip from '../../BayTooltip';
import { getValidUrl } from '../../utilities/otherFuncs';
import { urlRegex } from '../../utilities/regex';
import type { TranslationFunction } from '../../types/common';
import type { VoidCallback } from '../../types/callbacks';

import BayFieldWithSpinner from './BayFieldSpinner';
import BayLimiters from './BayLimiters';

// ===== Interfaces =====

interface ShowCompanyLinkDrawer {
	showDrawer: boolean;
	mode: 'Add' | 'Edit';
	isweb: boolean;
}

interface OfficeItem {
	label: string;
	value: string;
}

interface GroupItem {
	label: string;
	value: string;
}

interface SsoFields {
	INPUTFIELDS?: Record<string, SsoFieldConfig>;
	HIDDENFIELDS?: Record<string, SsoFieldConfig>;
}

interface SsoFieldConfig {
	ACCESS?: string;
	VALUES?: string;
	maxLength?: number;
}

interface SsoItem {
	label: string;
	value: string;
	description?: string;
	url: string;
	Name?: string;
	ID?: string;
	fields?: SsoFields;
	FIELDS?: SsoFields;
}

interface SsoOption {
	label: string;
	value: string;
}

interface CurrentData {
	Label?: string;
	SRC?: string;
	Target?: string;
	OfficeID_List?: string;
	AccessGroup_List?: string;
	Access_List?: string;
	ssoLogin_params?: string;
	singlesignon_parameters?: string;
	SingleSignOn_Parameters?: string;
	ssoDetails?: Record<string, SsoFieldDetailsItem>;
	[key: string]: any;
}

interface CurrentSSO {
	url?: string;
	description?: string;
	ID?: string;
	FIELDS?: SsoFields;
	fields?: SsoFields;
}

interface FieldsData {
	label: string;
	src: string;
	selectedSSO: string;
	target: string;
}

interface SsoFieldDetailsItem {
	access: string;
	values: string;
}

interface AccessLevels {
	admin: string;
	staff: string;
	users: string;
}

interface ValidationMessage {
	label: string;
	url: string;
}

interface QuickLinkService {
	processAgentSSO: (params: { linkFormData: Record<string, any> }) => Promise<ApiResponse>;
	processPersonalAndWebLinks: (params: { linkFormData: Record<string, any> }) => Promise<ApiResponse>;
}

interface ApiResponse {
	STATUS: number;
	MESSAGE?: string;
}

interface AddEditCompanyLinkDrawerProps {
	handleWebDrawerClose: VoidCallback;
	isSsoQuickLink: boolean;
	officeList: OfficeItem[];
	groupList: GroupItem[];
	ssoList: SsoItem[];
	showCompanyLinkDrawer: ShowCompanyLinkDrawer | null;
	currentData: CurrentData | null;
	currentSSO: CurrentSSO | null;
	setShowSnackbarHandler: (message: string, variant?: string) => void;
	fetchQuickLinksCallBack: VoidCallback;
	t: TranslationFunction;
	userSingleSignOnParameters: Record<string, any>;
	quickLinkService: QuickLinkService;
}

interface WebDrawerHeaderProps {
	showCompanyLinkDrawer: ShowCompanyLinkDrawer | null;
	resetFunction: VoidCallback;
}

interface DisabledTooltipWrapperProps {
	children: React.ReactElement;
	t: TranslationFunction;
}

// ===== Components =====

const WebDrawerHeader = ({ showCompanyLinkDrawer, resetFunction }: WebDrawerHeaderProps) => {
	return (
		<>
			<div className="d-flex flex-column w-100 m-2">
				<h2 className="fs-3">
					{`${showCompanyLinkDrawer?.mode} Company-wide ${showCompanyLinkDrawer?.isweb ? 'Web' : 'SSO'} Link`}
				</h2>
			</div>
			<CloseButton className="mb-3 pe-2" onClick={() => resetFunction()} />
		</>
	);
};

const DisabledTooltipWrapper = ({ children, t }: DisabledTooltipWrapperProps) => {
	return (
		<BayTooltip
			overlay={
				<>
					<p>{t('uneditableSsoDetailsNote')}</p>
					<p className="mb-0">{t('deleteSsoRequestNote')}</p>
				</>
			}
		>
			{children}
		</BayTooltip>
	);
};

const AddEditCompanyLinkDrawer = (props: AddEditCompanyLinkDrawerProps) => {
	const {
		handleWebDrawerClose,
		isSsoQuickLink,
		officeList,
		groupList,
		ssoList,
		showCompanyLinkDrawer,
		currentData,
		currentSSO,
		setShowSnackbarHandler,
		fetchQuickLinksCallBack,
		t,
		userSingleSignOnParameters,
		quickLinkService,
	} = props;
	const [isSelectOffices, setIsSelectOffices] = useState<boolean>(false);
	const [isSelectGroups, setIsSelectGroups] = useState<boolean>(false);
	const [selectedOption, setSelectedOption] = useState<string>('');
	const [fieldsData, setFieldsData] = useState<FieldsData>({
		label: '',
		src: '',
		selectedSSO: '',
		target: '_blank',
	});
	const [ssoFields, setSsoFields] = useState<string[]>([]);
	const [ssoFieldDetails, setSsoFieldDetails] = useState<Record<string, SsoFieldDetailsItem>>({});
	const [isAdminSelected, setIsAdminSelected] = useState<boolean>(false);
	const [ssoHidFields, setSsoHidFields] = useState<Record<string, SsoFieldDetailsItem>>({});
	const [accessLevels, setAccessLevels] = useState<AccessLevels>({
		admin: '',
		staff: '',
		users: '',
	});
	const [isSelectAccess, setIsSelectAccess] = useState<boolean>(false);
	const [isEmptyAccess, setIsEmptyAccess] = useState<boolean>(true);
	const [officeIdList, setOfficeIdList] = useState<string>('');
	const [accessGroupList, setAccessGroupList] = useState<string>('');
	const [selectedOfficesArr, setSelectedOfficesArr] = useState<any[]>([]);
	const [selectedAccessGroupArr, setSelectedAccessGroupArr] = useState<any[]>([]);
	const [SSO, setSSO] = useState<SsoOption | Record<string, never>>({});
	const [isValidUrl, setIsValidUrl] = useState<boolean>(false);
	const [validationMessage, setValidationMessage] = useState<ValidationMessage>({
		label: '',
		url: '',
	});
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [isUrlFieldLoading, setIsUrlFieldLoading] = useState<boolean>(false);

	const ssoOptionArray = useMemo<SsoOption[]>(() => {
		return ssoList?.map((e) => {
			return {
				label: e.label,
				value: e.value,
			};
		});
	}, [ssoList]);
	const isEdit = showCompanyLinkDrawer?.mode === 'Edit';
	const initialValues: FieldsData = {
		label: '',
		src: '',
		selectedSSO: '',
		target: '_blank',
	};
	const ssoAccess = [
		{ label: 'Admin/Company', value: 'A' },
		{ label: 'Users', value: 'U' },
	];

	useEffect(() => {
		if (currentData && Object.keys(currentData)?.length > 0) {
			let ssoValue = '';
			if (isSsoQuickLink && currentData?.SRC?.length && currentData.SRC.length >= 5) {
				ssoValue = currentData?.SRC?.split(':')[1];
			}
			isSsoQuickLink || (currentData?.SRC?.length ?? 0) > 0
				? setIsValidUrl(true)
				: setIsValidUrl(false);
			setFieldsData({
				label: currentData?.Label || '',
				src: isSsoQuickLink ? (currentSSO?.url ?? '') : (currentData?.SRC || ''),
				selectedSSO: ssoValue.toString() || '',
				target: currentData?.Target || '_blank',
			});
			currentData?.OfficeID_List?.length
				? setIsSelectOffices(true)
				: setIsSelectOffices(false);
			currentData?.AccessGroup_List?.length
				? setIsSelectGroups(true)
				: setIsSelectGroups(false);
			if (currentSSO && Object.keys(currentSSO)?.length) {
				setSSO(ssoOptionArray.find((a) => a.label === currentSSO?.description) ?? {});
				const inputFieldsKeys = Object.keys(currentSSO?.FIELDS?.INPUTFIELDS ?? {});
				if (inputFieldsKeys.length) {
					setSsoFields(inputFieldsKeys.map((field) => field.toLowerCase()));
					constructSsoFieldDetails(currentSSO?.FIELDS);
					setSelectedOption(currentSSO?.description ?? '');
				}
			}
			loadEditData();
		} else {
			setFieldsData(initialValues);
		}
	}, [currentData]);

	useEffect(() => {
		const ssoDetailsSet = (data: string[], dataObject: Record<string, SsoFieldConfig>) => {
			const initialSsoDetail: Record<string, SsoFieldDetailsItem> = {};
			data.forEach((a) => {
				initialSsoDetail[a] = {
					access: dataObject[a]?.ACCESS ?? '',
					values: dataObject[a]?.VALUES ?? '',
				};
			});
			Object.keys(initialSsoDetail)?.length &&
				checkAdminSelected(initialSsoDetail);
			setSsoFieldDetails(initialSsoDetail);
		};
		if (!isDataLoaded) {
			ssoList?.find((a) => {
				if (a?.value === selectedOption || a?.label === selectedOption) {
					const fieldsDataObject = a?.fields || {};
					let inputFieldsKeys: string[] = [];
					if (Object.keys(fieldsDataObject).length) {
						inputFieldsKeys =
							Object.keys(fieldsDataObject?.INPUTFIELDS ?? {}) || [];
						inputFieldsKeys = inputFieldsKeys.map((field) =>
							field.toLowerCase(),
						);
						constructSsoFieldDetails(fieldsDataObject);
						ssoDetailsSet(inputFieldsKeys, fieldsDataObject?.INPUTFIELDS ?? {});
					}
					setSsoFields(inputFieldsKeys);
				}
			});
		}
	}, [selectedOption]);

	const checkAdminSelected = (updatedState: Record<string, SsoFieldDetailsItem>) => {
		const emptyValue = Object.keys(updatedState).find(
			(a) =>
				(updatedState[a]?.access === 'A' && !updatedState[a]?.values) ||
				!updatedState[a]?.access,
		);

		if (emptyValue?.length) {
			setIsAdminSelected(true);
		} else {
			setIsAdminSelected(false);
		}
	};

	const resetFunction = () => {
		setFieldsData({ ...initialValues });
		setSSO({});
		setAccessLevels({ admin: '', staff: '', users: '' });
		setIsSelectAccess(false);
		setIsEmptyAccess(true);
		setSsoFieldDetails({});
		setSelectedOfficesArr([]);
		setIsSelectOffices(false);
		setIsSelectGroups(false);
		setSsoFields([]);
		setOfficeIdList('');
		setSelectedAccessGroupArr([]);
		setAccessGroupList('');
		setIsAdminSelected(false);
		handleWebDrawerClose();
		setSsoHidFields({});
		setIsValidUrl(false);
		setValidationMessage({ label: '', url: '' });
		setSelectedOption('');
		setIsDataLoaded(false);
	};

	const constructSsoFieldDetails = (selectedSso: SsoFields = {}) => {
		if (Object.keys(selectedSso).length) {
			const inputHFieldsDetails = selectedSso?.HIDDENFIELDS
				? selectedSso?.HIDDENFIELDS
				: {};
			const inputHFieldKeys = Object.keys(inputHFieldsDetails);
			const inputHideDetail: Record<string, SsoFieldDetailsItem> = {};
			if (inputHFieldKeys?.length) {
				inputHFieldKeys.forEach((a) => {
					inputHideDetail[a] = {
						access: inputHFieldsDetails[a]?.ACCESS ?? '',
						values: inputHFieldsDetails[a]?.VALUES ?? '',
					};
				});
			}
			setSsoHidFields(inputHideDetail);
		}
	};

	const checkValidField = async (fieldName: string, value: string) => {
		let errorMessage = '';
		if (fieldName === 'label') {
			errorMessage = !value?.trim()?.length ? t('linkCannotBlank') : '';
			setValidationMessage((prevMessage) => ({
				...prevMessage,
				label: errorMessage,
			}));
		} else {
			const url = value.trim();
			if (!url?.length) {
				errorMessage = t('urlCannotBlank');
				setIsValidUrl(false);
			} else if (urlRegex.test(url)) {
				const response = await getValidUrl(url, setIsUrlFieldLoading);
				if (response.STATUS === 0 || response.STATUS === 2) {
					const updatedFieldsData = {
						...fieldsData,
						['src']: response.validURL,
					};
					setFieldsData(updatedFieldsData);
					setIsValidUrl(true);
				}
			} else {
				errorMessage = t('invalidUrlNote');
				setIsValidUrl(false);
			}
			setValidationMessage((prevMessage) => ({
				...prevMessage,
				url: errorMessage,
			}));
		}
	};

	const handleFieldChange = (key: string, value: string) => {
		const updatedFieldsData = {
			...fieldsData,
			[key]: value,
		};
		setFieldsData(updatedFieldsData);
		if (key === 'src' && !isSsoQuickLink) {
			setIsValidUrl(false);
			setValidationMessage((prevMessage) => ({ ...prevMessage, url: '' }));
		}
	};

	const OnSsoSelect = (event: { value: string; label: string }) => {
		setSsoFieldDetails({});
		setIsValidUrl(true);
		setSelectedOption(event.value);
		setSSO(ssoOptionArray.find((a) => a.value === event.value) ?? {});
		isSsoQuickLink &&
			handleFieldChange(
				'src',
				ssoList.find((a) => a.value === event.value)?.url ?? '',
			);
	};

	const handleSelectLimiters = (formInput: string, selectedValues: any[]) => {
		let SelectionList = selectedValues;
		if (selectedValues?.map((a) => a?.value)?.includes('all')) {
			SelectionList = [{ label: 'All', value: 'all' }];
		}
		const selectedList = SelectionList?.map((e) => e.value).join(',');
		if (formInput === 'members') {
			setSelectedOfficesArr(selectedValues);
			const resultOfficeIdList =
				selectedList === 'all'
					? officeList?.map((e) => e?.value).join(',')
					: selectedList;
			setOfficeIdList(resultOfficeIdList);
		} else {
			setSelectedAccessGroupArr(selectedValues);
			const resultGroupIdList =
				selectedList === 'all'
					? groupList?.map((e) => e?.value).join(',')
					: selectedList;
			setAccessGroupList(resultGroupIdList);
		}
	};

	const handleSsoDetailsChange = (section: string, field: string, updatevalue: string = '') => {
		if (field === 'access' && !isAdminSelected && updatevalue !== 'U') {
			setIsAdminSelected(true);
		}
		const currentID = currentSSO?.ID?.length
			? currentSSO?.ID
			: (SSO as SsoOption)?.value?.slice(-2);
		const initialSectionValue = ssoFieldDetails[section]?.values
			? ssoFieldDetails[section]?.values
			: '';
		const resetFieldValue =
			field === 'access' && updatevalue === 'U'
				? currentID === '28'
					? { values: initialSectionValue }
					: { values: '' }
				: {};
		setSsoFieldDetails((prevState) => {
			const updatedState = {
				...prevState,
				[section]: {
					...prevState[section],
					[field]: updatevalue,
					...resetFieldValue,
				},
			};
			checkAdminSelected(updatedState);
			return updatedState;
		});
	};
	const loadEditData = () => {
		const isNullvalue = (value: any) => {
			return value === null || value === '' || value === undefined;
		};
		// TODO: Clearly we need to refactor this code
		const transformFilters = (
			linksObject: string,
			isSelectedOffices: boolean = false,
			isSelectedGroups: boolean = false,
			isMulti: boolean = true,
		) => {
			let selectOptions: any = null;
			if (linksObject) {
				const hasMultipleOptions = linksObject?.includes(',');
				if (isMulti) {
					const tempArray: any[] = [];
					let selectedOptions: any = {};
					let label = '';
					// eslint-disable-next-line no-unsafe-optional-chaining
					for (let option of hasMultipleOptions
						// eslint-disable-next-line no-unsafe-optional-chaining
						? linksObject?.split(',')
						: [linksObject]) {
						if (isSelectedOffices) {
							// eslint-disable-next-line no-unsafe-optional-chaining
							selectedOptions = officeList?.find((a) => a?.value === option);
							label = selectedOptions?.label || '';
							// eslint-disable-next-line no-self-assign
							option = option;
						}
						if (isSelectedGroups) {
							// eslint-disable-next-line no-unsafe-optional-chaining
							selectedOptions = groupList?.find((a) => a?.value === option);
							label = selectedOptions?.label || '';
							// eslint-disable-next-line no-self-assign
							option = option;
						}
						tempArray?.push({ label, value: option });
					}

					selectOptions = tempArray;

					if (isSelectedOffices) {
						selectOptions =
							tempArray?.length === officeList?.length
								? [{ label: 'All', value: 'all' }]
								: tempArray;
					}

					if (isSelectedGroups) {
						selectOptions =
							tempArray?.length === groupList?.length
								? [{ label: 'All', value: 'all' }]
								: tempArray;
					}
				} else {
					selectOptions = { label: linksObject, value: linksObject };
				}
			}
			return selectOptions;
		};

		const dataSet = (datas: string) => {
			const hasMultipleOption = datas?.includes(',');
			if (!datas.length) {
				setAccessLevels({ admin: '', staff: '', users: '' });
				setIsSelectAccess(false);
			} else {
				setIsSelectAccess(true);
			}
			// eslint-disable-next-line no-unsafe-optional-chaining
			for (const option of hasMultipleOption ? datas?.split(',') : [datas]) {
				if (option?.trim() === 'A') {
					setAccessLevels((prevAccess) => ({ ...prevAccess, admin: 'A' }));
				} else if (option?.trim() === 'S') {
					setAccessLevels((prevAccess) => ({ ...prevAccess, staff: 'S' }));
				} else if (option?.trim() === 'U') {
					setAccessLevels((prevAccess) => ({ ...prevAccess, users: 'U' }));
				}
			}
		};

		const loadSsoDetails = (data: any, linkType: string) => {
			const initialSsoValues: Record<string, SsoFieldDetailsItem> = {};
			if (linkType === 'S') {
				if (data?.ssoDetails) {
					Object.entries(data?.ssoDetails).forEach(
						([SSOfieldName, SSOfieldDetails]: [string, any]) => {
							//TODO: Refactor this to use hasOwnProperty
							// eslint-disable-next-line no-prototype-builtins
							if (SSOfieldDetails.hasOwnProperty('access'))
								initialSsoValues[SSOfieldName] =
									SSOfieldDetails.access === 'U' &&
									ssoList[0]?.value?.slice(-2) !== '28'
										? { ...SSOfieldDetails, values: '' }
										: SSOfieldDetails;
						},
					);
				} else {
					const ssoConfigurationInputFields = ssoList[0]?.fields?.INPUTFIELDS ?? {};
					Object.entries(ssoConfigurationInputFields).forEach(
						([key, value]: [string, any]) => {
							if (
								Object.keys(data)?.length &&
								data[key] !== undefined &&
								value?.ACCESS === 'A'
							) {
								initialSsoValues[key] = {
									access: value.ACCESS,
									values: data[key],
								};
							} else {
								initialSsoValues[key] = {
									access: value.ACCESS,
									values:
										value.ACCESS === 'A'
											? userSingleSignOnParameters[key]
											: ssoList[0]?.value?.slice(-2) === '28'
												? data[key]
												: '',
								};
							}
						},
					);
					checkAdminSelected(initialSsoValues);
				}
			}
			setSsoFieldDetails(initialSsoValues);
			setIsDataLoaded(true);
		};

		if (!currentData) return;

		for (const key in currentData) {
			const linkObject = currentData[key];
			const linkType = currentData?.SRC?.includes('sso:') ? 'S' : 'W';
			const isNoSsoLoginParams =
				currentData?.ssoLogin_params === '' ? true : false;
			switch (key) {
				case 'OfficeID_List':
					setSelectedOfficesArr(
						isNullvalue(linkObject) ? [] : transformFilters(linkObject, true),
					);
					setOfficeIdList(linkObject);
					break;
				case 'Access_List':
					dataSet(linkObject);
					break;
				case 'AccessGroup_List':
					setSelectedAccessGroupArr(
						isNullvalue(linkObject)
							? []
							: transformFilters(linkObject, false, true),
					);
					setAccessGroupList(linkObject);
					break;
				case 'ssoLogin_params':
					if (linkType === 'S') {
						const data = isNoSsoLoginParams
							? currentData['SingleSignOn_Parameters']
							: linkObject;
						loadSsoDetails(JSON.parse(data), linkType);
					}
					break;
				case 'singlesignon_parameters':
					if (linkType === 'W') {
						loadSsoDetails(JSON.parse(linkObject), linkType);
					}
					break;
				default:
					break;
			}
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const linkFormData: Record<string, any> = {};
		linkFormData['officeid_list'] = isSelectOffices ? officeIdList : '';
		linkFormData['accessgroup_list'] = isSelectGroups ? accessGroupList : '';
		linkFormData['label'] = fieldsData?.label;
		linkFormData['link_type'] = isSsoQuickLink ? 'S' : 'W';
		linkFormData['sso_src'] = isSsoQuickLink ? (SSO as SsoOption)?.value : '';
		linkFormData['web_src'] = !isSsoQuickLink ? fieldsData?.src : '';
		linkFormData['target'] = fieldsData?.target ?? '_blank';
		isEdit && (linkFormData['OrigSRC'] = currentData?.SRC);
		linkFormData['action'] = isEdit ? 'update' : 'create';
		linkFormData['Method'] = 'ProcessLinks';
		linkFormData['publicid'] = 'global';
		const isAccessLevelEmpty = Object.values(accessLevels).every(
			(val) => val === '' || val === null,
		);
		const accessLevelsArray = isAccessLevelEmpty
			? ''
			: [accessLevels?.admin, accessLevels?.staff, accessLevels?.users];
		linkFormData.access_list = isAccessLevelEmpty
			? ''
			: Array.isArray(accessLevelsArray)
				? accessLevelsArray
					.filter((value) => {
						return value?.trim() !== '';
					})
					.join(',')
				: '';
		let loginParamsObject: Record<string, any> = {};
		if (Object.keys(ssoFieldDetails).length) {
			loginParamsObject = {
				ssoDetails: { ...ssoFieldDetails, ...ssoHidFields },
			};
		}
		if (isSsoQuickLink) {
			linkFormData['sso_src'] = isSsoQuickLink ? (SSO as SsoOption)?.value : '';
			linkFormData['ssoType'] = isSsoQuickLink
				? ssoList.find((a) => a.value === (SSO as SsoOption)?.value)?.Name
				: '';
			linkFormData['web_src'] = fieldsData?.src;
			linkFormData['loginParams'] = JSON.stringify(loginParamsObject);

			let ssoDirectData: Record<string, any> = {};
			const data = loginParamsObject.ssoDetails;
			Object.keys(data).forEach((key) => {
				ssoDirectData[key] = data[key]?.values ?? data[key]?.VALUES;
			});
			ssoDirectData = { ...ssoDirectData, ...linkFormData };
			delete ssoDirectData?.publicid;
			delete ssoDirectData?.loginParams;
			delete ssoDirectData?.ssoType;
			delete ssoDirectData?.action;
			delete ssoDirectData?.OrigSRC;
			linkFormData['params'] = JSON.stringify(ssoDirectData);
		}

		const Response = isSsoQuickLink
			? await quickLinkService.processAgentSSO({
					linkFormData,
				})
			: await quickLinkService.processPersonalAndWebLinks({
					linkFormData,
				});
		resetFunction();
		if (Response.STATUS === 0) {
			setShowSnackbarHandler(!isEdit ? t('linkAdded') : t('linkUpdated'));
			fetchQuickLinksCallBack();
		} else {
			setShowSnackbarHandler(Response?.MESSAGE ?? '', 'danger');
		}
	};

	return (
		<BayFilterDrawer
			autoFocus={false}
			backdrop="static"
			enforceFocus={false}
			handleCloseDrawer={() => resetFunction()}
			headerChildren={
				<WebDrawerHeader
					resetFunction={resetFunction}
					showCompanyLinkDrawer={showCompanyLinkDrawer}
				/>
			}
			isDynamicWidth={true}
			restoreFocus={false}
			showDrawer={showCompanyLinkDrawer?.showDrawer ?? false}
		>
			<div className="add-links company-wide-drawer m-2">
				<Form id="quicklinks_dataform" onSubmit={(e) => handleSubmit(e)}>
					<Row>
						<Form.Label className="fs-6 fw-normal text-900">
							{showCompanyLinkDrawer?.isweb
								? t('webDrawerDesc')
								: t('ssoDrawerDesc')}
						</Form.Label>
					</Row>
					<Row className="my-2">
						<Form.Group>
							<Form.Label className="d-flex justify-content-between">
								<div className="required">{t('linkLabel')}</div>
								<span className="text-500 fw-normal">
									{fieldsData?.label?.length} / 40
								</span>
							</Form.Label>
							<FormControl
								id="label"
								isInvalid={validationMessage?.label?.length > 0}
								maxLength={40}
								name="label"
								placeholder={t('labelPlaceholder')}
								type="text"
								value={fieldsData?.label}
								onBlur={(e: ChangeEvent<HTMLInputElement>) => checkValidField(e.target.id, e.target.value)}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									handleFieldChange(e.target.id, e.target.value);
									setValidationMessage((prevMessage) => ({
										...prevMessage,
										label: '',
									}));
								}}
							/>
							<Form.Control.Feedback
								className={
									validationMessage?.label?.length > 0 ? 'd-block' : ''
								}
								type="invalid"
							>
								{validationMessage?.label}
							</Form.Control.Feedback>
						</Form.Group>
					</Row>
					<Row className="my-3">
						<Form.Group>
							<Form.Label className="d-flex flex-wrap justify-content-between">
								<div className="required">{t('toWhatUrlLinkgo')}</div>
								{!isValidUrl ? (
									<span className="text-400 fw-normal">
										{t('testThisLink')}
									</span>
								) : (
									<a
										className="text-blue fw-normal"
										href={fieldsData?.src}
										target="_blank"
									>
										{t('testThisLink')}
									</a>
								)}
							</Form.Label>
							{isEdit && isSsoQuickLink ? ( // show disabled field with tooltip while editing SSO
								<DisabledTooltipWrapper t={t}>
									<span>
										<FormControl
											disabled={isSsoQuickLink}
											id="src"
											maxLength={512}
											minLength={4}
											name="src"
											placeholder={t('websitePlaceholder')}
											type="text"
											value={fieldsData?.src}
										/>
									</span>
								</DisabledTooltipWrapper>
							) : (
								<>
									<BayFieldWithSpinner
										disabled={isSsoQuickLink} //disabling link field for SSO types
										id="src"
										isFieldLoading={isUrlFieldLoading}
										isInvalid={
											validationMessage?.length > 0 &&
											!isSsoQuickLink &&
											fieldsData?.src?.length > 0
										}
										maxLength={512}
										minLength={4}
										name="src"
										placeholder={t('websitePlaceholder')}
										value={fieldsData?.src}
										onBlurFunc={(e: ChangeEvent<HTMLInputElement>) =>
											!isValidUrl && !validationMessage?.url?.length
												? checkValidField(e.target.id, e.target.value)
												: () => {}
										}
										onChangeFunc={(e: ChangeEvent<HTMLInputElement>) =>
											handleFieldChange(e.target.id, e.target.value)
										}
									/>
									<Form.Control.Feedback
										className={
											validationMessage?.url?.length > 0 && !isSsoQuickLink
												? 'd-block'
												: ''
										}
										type="invalid"
									>
										{validationMessage?.url}
									</Form.Control.Feedback>
								</>
							)}
						</Form.Group>
					</Row>
					{isSsoQuickLink && (
						<>
							<Row className="my-3">
								<Form.Group>
									<Form.Label className="required">{t('sso')}</Form.Label>
									{isEdit ? (
										<DisabledTooltipWrapper t={t}>
											<span>
												<BaySelect
													className="cursor-not-allowed"
													defaultValue={Object.keys(SSO).length ? SSO as any : undefined}
													disabled={isEdit}
													id="sso"
													name="sso"
												/>
											</span>
										</DisabledTooltipWrapper>
									) : (
										<BaySelect
											id="sso"
											isClearable={false}
											name="sso"
											optionsArray={ssoOptionArray}
											placeholder={t('select')}
											// eslint-disable-next-line no-unused-vars
											onChangeFunc={(selectedValue: any, meta: any) => {
												OnSsoSelect(selectedValue);
												setSsoFieldDetails({});
											}}
										/>
									)}
								</Form.Group>
							</Row>
							{ssoFields?.length ? (
								<Row className="my-3">
									<Form.Label>{t('ssoDetails')}</Form.Label>
									{ssoFields?.map((fieldName, index) => {
										const fieldNameLabel = fieldName
											.toLowerCase()
											.split('_')
											.join('')
											.replace(/[^\w\s]/gi, '');
										return (
											<Container
												key={index}
												className="ssofield ms-3 my-2 py-3 px-4 border rounded-1"
											>
												<Row className="text-capitalize">
													<Form.Label>
														{fieldName === 'time'
															? 'Time Stamp'
															: t(fieldNameLabel)}
													</Form.Label>
												</Row>
												<Row className="py-2 align-items-center px-3">
													<Col className="py-1 px-0" xl={6} xs={12}>
														<Form.Label className="mb-0">
															{t('whoNeedsToProvide')}
														</Form.Label>
													</Col>
													<Col className="py-1 px-0" xl={6} xs={12}>
														<div className="d-flex justify-content-start justify-content-xl-end align-items-center">
															<div className="selectAccessSection">
																<BaySelect
																	defaultValue={
																		ssoAccess.find(
																			(a) =>
																				a.value ===
																				ssoFieldDetails[fieldName]?.access,
																		) as any
																	}
																	id={`${fieldName}_access`}
																	isClearable={false}
																	name={`${fieldName}_access`}
																	optionsArray={ssoAccess}
																	onChangeFunc={(selectedValue: any, meta: any) => {
																		handleSsoDetailsChange(
																			fieldName,
																			'access',
																			selectedValue?.value,
																		);
																	}}
																/>
															</div>
															<BayTooltip
																mouseLeaveDelay={0.1}
																overlay={
																	<div style={{ width: '220px' }}>
																		<p>
																			<span className="fw-bold pe-2">
																				{t('ssoDetailNotesPart1')}
																			</span>
																			{t('ssoDetailNotesPart2')}
																		</p>
																		<p className="mb-0">
																			<span className="fw-bold pe-2">
																				{t('ssoDetailNotesPart3')}
																			</span>
																			{t('ssoDetailNotesPart4')}
																		</p>
																	</div>
																}
																overlayClassName="ssoDetailTooltip"
																placement="topRight"
															>
																<span className="ps-2 cursor-pointer">
																	<QuestionCircle
																		className="text-700"
																		size={16}
																	/>
																</span>
															</BayTooltip>
														</div>
													</Col>
												</Row>
												<FormControl
													className={
														ssoFieldDetails[fieldName]?.access !== 'A' &&
														'text-500 border-0'
													}
													disabled={ssoFieldDetails[fieldName]?.access !== 'A'}
													id={fieldName}
													maxLength={fieldName?.length ?? 32}
													name={fieldName}
													placeholder={t('enterDetails')}
													type="text"
													value={ssoFieldDetails[fieldName]?.values}
													onChange={(e: ChangeEvent<HTMLInputElement>) =>
														handleSsoDetailsChange(
															fieldName,
															'values',
															e.target?.value,
														)
													}
												/>
											</Container>
										);
									})}
								</Row>
							) : null}
						</>
					)}
					<BayLimiters
						accessLevels={accessLevels}
						currentData={currentData as any}
						groupList={groupList}
						handleSelectLimiters={handleSelectLimiters}
						isEmptyAccess={isEmptyAccess}
						isSelectAccess={isSelectAccess}
						isSelectGroups={isSelectGroups}
						isSelectOffices={isSelectOffices}
						officeList={officeList}
						selectedAccessGroupArr={selectedAccessGroupArr}
						selectedOfficesArr={selectedOfficesArr}
						setAccessLevels={setAccessLevels}
						setIsEmptyAccess={setIsEmptyAccess}
						setIsSelectAccess={setIsSelectAccess}
						setIsSelectGroups={setIsSelectGroups}
						setIsSelectOffices={setIsSelectOffices}
						t={t}
					/>
					<Row className="my-3 mb-5">
						<div className="d-flex align-items-center justify-content-between position-absolute bottom-0 start-0 border-top bg-white pt-3 px-4 company-wide-drawer-footer">
							<Form.Group className="form-item">
								<div className="ms-2">
									<Button
										className="btn-outline-800 border-500"
										name="QuickLinkCancelButton"
										variant="outline-dark"
										onClick={() => resetFunction()}
									>
										{' '}
										{t('cancel')}{' '}
									</Button>
								</div>
							</Form.Group>
							<Form.Group className="form-item d-flex align-items-center">
								<Button
									disabled={
										!fieldsData?.label?.length ||
										isAdminSelected ||
										(isSelectOffices && !officeIdList?.length) ||
										(!isSsoQuickLink &&
											!fieldsData?.src?.length &&
											!isValidUrl) ||
										(isSsoQuickLink && !Object.keys(SSO || {}).length) ||
										!isValidUrl ||
										validationMessage?.url?.length > 0 ||
										validationMessage?.label?.length > 0 ||
										(isSelectAccess && isEmptyAccess) ||
										(isSelectGroups && !accessGroupList.length)
									}
									name="QuickLinkAddButton"
									type="submit"
									variant="primary"
								>
									{!isEdit ? t('add') : t('save')}
								</Button>
							</Form.Group>
						</div>
					</Row>
				</Form>
			</div>
		</BayFilterDrawer>
	);
};

export default AddEditCompanyLinkDrawer;
